import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  IPerformanceReportForProfessionalsFilters,
  PerformanceReportForProfessionalsDetailedRow,
  PerformanceReportForProfessionalsEvolutionItem,
  PerformanceReportForProfessionalsRankingOrderColumn,
  PerformanceReportForProfessionalsRankingOrderDirection,
  PerformanceReportForProfessionalsSummary,
  PerformanceReportForProfessionalsTopValidatedActivitiesItem,
} from 'presentation/app/modules/Reports/models/performance-report-for-professionals';

interface IPerformanceReportForProfessionalsState {
  total: number;
  search: string;
  currentPage: number;
  rowsPerPage: number;
  summary: PerformanceReportForProfessionalsSummary;
  filters: IPerformanceReportForProfessionalsFilters;
  evolution: PerformanceReportForProfessionalsEvolutionItem[];
  detailedList: PerformanceReportForProfessionalsDetailedRow[];
  orderColumn: PerformanceReportForProfessionalsRankingOrderColumn;
  orderDirection: PerformanceReportForProfessionalsRankingOrderDirection;
  topValidatedActivities: PerformanceReportForProfessionalsTopValidatedActivitiesItem[];
}

const initialState: IPerformanceReportForProfessionalsState = {
  total: 0,
  search: '',
  filters: null,
  summary: null,
  evolution: [],
  currentPage: 0,
  rowsPerPage: 10,
  detailedList: [],
  orderDirection: 'ASC',
  topValidatedActivities: [],
  orderColumn: 'NAME',
};

export const PerformanceReportForProfessionalsSlice = createSlice({
  name: 'performance-report-for-professionals',
  initialState,
  reducers: {
    loadPerformanceReportForProfessionalsFilters: (
      state,
      action: PayloadAction<IPerformanceReportForProfessionalsFilters>,
    ) => {
      state.filters = action.payload;
    },

    loadPerformanceReportForProfessionalsSummary: (
      state,
      action: PayloadAction<PerformanceReportForProfessionalsSummary>,
    ) => {
      state.summary = action.payload;
    },

    loadPerformanceReportForProfessionalsEvolution: (
      state,
      action: PayloadAction<PerformanceReportForProfessionalsEvolutionItem[]>,
    ) => {
      state.evolution = action.payload;
    },

    loadPerformanceReportForProfessionalsTopValidatedActivities: (
      state,
      action: PayloadAction<
        PerformanceReportForProfessionalsTopValidatedActivitiesItem[]
      >,
    ) => {
      state.topValidatedActivities = action.payload;
    },

    loadPerformanceReportForProfessionalsDetailedList: (
      state,
      action: PayloadAction<PerformanceReportForProfessionalsDetailedRow[]>,
    ) => {
      state.detailedList = action.payload;
    },

    loadPerformanceReportForProfessionalsTotal: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.total = action.payload;
    },

    loadPerformanceReportForProfessionalsCurrentPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.currentPage = action.payload;
    },

    loadPerformanceReportForProfessionalsRowsPerPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.rowsPerPage = action.payload;
    },

    loadPerformanceReportForProfessionalsOrder: (
      state,
      action: PayloadAction<{
        orderColumn: PerformanceReportForProfessionalsRankingOrderColumn;
        orderDirection: PerformanceReportForProfessionalsRankingOrderDirection;
      }>,
    ) => {
      state.orderColumn = action.payload.orderColumn;
      state.orderDirection = action.payload.orderDirection;
    },

    loadPerformanceReportForProfessionalsSearch: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.search = action.payload;
    },

    clearPerformanceReportForProfessionalsData: (state) => {
      state.summary = null;
      state.evolution = [];
      state.topValidatedActivities = [];
      state.detailedList = [];
      state.total = 0;
    },
  },
});

export const {
  loadPerformanceReportForProfessionalsOrder,
  clearPerformanceReportForProfessionalsData,
  loadPerformanceReportForProfessionalsTotal,
  loadPerformanceReportForProfessionalsSearch,
  loadPerformanceReportForProfessionalsFilters,
  loadPerformanceReportForProfessionalsSummary,
  loadPerformanceReportForProfessionalsEvolution,
  loadPerformanceReportForProfessionalsCurrentPage,
  loadPerformanceReportForProfessionalsRowsPerPage,
  loadPerformanceReportForProfessionalsDetailedList,
  loadPerformanceReportForProfessionalsTopValidatedActivities,
} = PerformanceReportForProfessionalsSlice.actions;

export default PerformanceReportForProfessionalsSlice.reducer;
